import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ── Polling interval constant ─────────────────────────────────────────────────
// Centralised here so every consumer references the same source of truth.
// Background polling is disabled via refetchIntervalInBackground: false —
// TanStack Query natively handles tab-visibility pausing when this is set.
export const NOTIFICATION_POLL_INTERVAL_MS = 30_000;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  type: number;
  titleKey: string;
  bodyKey: string | null;
  params: Record<string, string> | null;
  link?: string | null;
  entityId?: string | null;
  isRead: boolean;
  createdAt: string;
}

/**
 * Backend NotificationType enum'unun mirror'i. Yeni tip eklendiginde hem domain
 * (Beetegre.Domain.Entities.NotificationType) hem burasi guncellenmeli — frontend
 * type'a gore farkli aksiyon (ornegin BulkJobCompleted -> JobResultDrawer) ihtiyacinda
 * magic-number switch'ten kaciniliyor.
 */
export const NotificationType = {
  OrderCreated: 1,
  OrderStatus: 2,
  InvoiceCreated: 3,
  StockLow: 4,
  IntegrationError: 5,
  CheckNoteDueSoon: 6,
  BulkJobCompleted: 7,
  UnresolvedItemsAging: 8,
  RetentionExemptionDecision: 9,
  SubscriptionRenewalReminder: 10,
  Generic: 99,
} as const;

/**
 * Backend artik ParamsJson'i ayri bir alanda (jsonb, compact JSON string veya null) donuyor —
 * AB#1365 oncesindeki in-band `key|json` packing (parseKeyedMessage) kaldirildi. Ham string'i
 * tuketilebilir bir obje haline getirir; bozuk/null JSON sessizce null'a duser ve caller
 * interpolasyonsuz (paramsiz) t() cagrisina geri doner.
 */
function parseParamsJson(raw: string | null | undefined): Record<string, string> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return null;
  }
}

// ── Base URL helpers ──────────────────────────────────────────────────────────

const base = (tenantId: string) => `/api/v1/tenants/${tenantId}/notifications`;

// ── Query-key factories ───────────────────────────────────────────────────────

export const notificationKeys = {
  all:         (tenantId: string) => ['notifications', tenantId] as const,
  unreadCount: (tenantId: string) => ['notifications', 'unread', tenantId] as const,
  list:        (tenantId: string) => ['notifications', 'list', tenantId] as const,
};

// ── Query hooks ───────────────────────────────────────────────────────────────

/**
 * Polls the unread-count badge every 30 s while the tab is visible.
 * Replaces the manual setInterval + visibilitychange pattern.
 */
export function useUnreadNotificationCount(tenantId: string | undefined) {
  return useQuery<number>({
    queryKey: notificationKeys.unreadCount(tenantId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get(`${base(tenantId!)}/unread-count`, { signal })
        .then((r) => r.data?.count ?? 0),
    enabled: !!tenantId,
    staleTime: NOTIFICATION_POLL_INTERVAL_MS,
    refetchInterval: NOTIFICATION_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

/** Raw list-endpoint shape before params parsing — API returns `paramsJson` as a JSON string. */
interface RawNotificationItem extends Omit<NotificationItem, 'params'> {
  paramsJson?: string | null;
}

/**
 * Fetches the notification list/dropdown payload (most recent 20).
 * Uses the same 30 s poll so the dropdown is never stale after it opens.
 */
export function useNotifications(tenantId: string | undefined, enabled = true) {
  return useQuery<NotificationItem[]>({
    queryKey: notificationKeys.list(tenantId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get(`${base(tenantId!)}?limit=20`, { signal })
        .then((r) => (Array.isArray(r.data) ? (r.data as RawNotificationItem[]) : []))
        .then((items) =>
          items.map(({ paramsJson, ...rest }) => ({
            ...rest,
            params: parseParamsJson(paramsJson),
          })),
        ),
    enabled: !!tenantId && enabled,
    staleTime: NOTIFICATION_POLL_INTERVAL_MS,
    refetchInterval: NOTIFICATION_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

/** Mark a single notification as read. Invalidates both unread count and list. */
export function useMarkNotificationRead(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      api.post(`${base(tenantId)}/${notificationId}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.unreadCount(tenantId) });
      qc.invalidateQueries({ queryKey: notificationKeys.list(tenantId) });
    },
    meta: { errorToast: true },
  });
}

/** Mark all notifications as read. Invalidates both unread count and list. */
export function useMarkAllNotificationsRead(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(tenantId)}/read-all`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.unreadCount(tenantId) });
      qc.invalidateQueries({ queryKey: notificationKeys.list(tenantId) });
    },
    meta: { errorToast: true },
  });
}
